import { useMemo } from 'react';

import type { UseAtAGlanceOptions } from './atAGlanceTypes.ts';
import { buildAtAGlanceInput } from './buildAtAGlanceInput.ts';

export const useAtAGlanceRequestBody = (
	{
		enabled,
		contentId,
		contentType,
		connections,
		pageTitle,
		pageAdf,
		pageVersion,
		pageUpdatedAt,
		structuralAncestors,
		outgoingLinksLoading = false,
	}: UseAtAGlanceOptions,
	{ cloudId, locale }: { cloudId: string | undefined; locale: string },
): { requestBody: string | null; waitingOnPrereqs: boolean } => {
	const relatedContentLoading = Boolean(
		connections.relatedContent.enabled && connections.relatedContent.loading,
	);

	const requestBody = useMemo(() => {
		if (
			!enabled ||
			!contentId ||
			!cloudId ||
			!pageAdf ||
			typeof pageTitle !== 'string' ||
			connections.appearsIn.loading ||
			outgoingLinksLoading ||
			relatedContentLoading
		) {
			return null;
		}

		const input = buildAtAGlanceInput({
			cloudId,
			contentId,
			contentType,
			locale,
			pageTitle,
			pageVersion,
			pageUpdatedAt,
			pageAdf,
			structuralAncestors,
			incomingLinks: connections.appearsIn.links,
			outgoingLinks: connections.linksTo.links,
			relatedContentLinks: connections.relatedContent.links,
		});
		if (!input) {
			return null;
		}

		return JSON.stringify({ ai_feature_input: input });
	}, [
		enabled,
		pageAdf,
		pageTitle,
		pageUpdatedAt,
		pageVersion,
		structuralAncestors,
		cloudId,
		connections.appearsIn.links,
		connections.appearsIn.loading,
		outgoingLinksLoading,
		relatedContentLoading,
		connections.linksTo.links,
		connections.relatedContent.links,
		contentId,
		contentType,
		locale,
	]);

	const waitingOnPrereqs = Boolean(
		contentId &&
		cloudId &&
		(!pageAdf ||
			pageTitle === undefined ||
			connections.appearsIn.loading ||
			outgoingLinksLoading ||
			relatedContentLoading),
	);

	return { requestBody, waitingOnPrereqs };
};
